/**
 * ZzFX sample generator, vendored from ZzFXMicro v1.4.0 (https://github.com/KilledByAPixel/ZzFX).
 *
 * MIT License - Copyright (c) 2019 Frank Force. Permission is hereby granted, free of charge, to any person
 * obtaining a copy of this software, to deal in the Software without restriction (full text:
 * https://github.com/KilledByAPixel/ZzFX/blob/master/LICENSE). The above copyright notice and this
 * permission notice shall be included in all copies or substantial portions of the Software.
 *
 * Changes from the original: typed, pure (no AudioContext, no global volume `zzfxV`), sample rate and the
 * random source are parameters, output is a Float32Array. Playback goes through our own mixer
 * (sfxBank.ts) so volume settings, mute, iOS unlock and distance all apply. Design sounds in the web
 * designer (https://killedbyapixel.github.io/ZzFX/) and paste the parameter array into sfxPresets.ts.
 */

/**
 * [volume, randomness, frequency, attack, sustain, release, shape, shapeCurve, slide, deltaSlide,
 *  pitchJump, pitchJumpTime, repeatTime, noise, modulation, bitCrush, delay, sustainVolume, decay,
 *  tremolo, filter] — shape 0 sin, 1 triangle, 2 saw, 3 tan, 4 noise, 5 square; filter > 0 highpass Hz,
 *  < 0 lowpass Hz. Trailing values may be left out (defaults below).
 */
export type ZzfxParams = readonly (number | undefined)[];

export function zzfxSamples(params: ZzfxParams, sampleRate = 44100, rand: () => number = Math.random): Float32Array {
  let [
    volume = 1,
    randomness = 0.05,
    frequency = 220,
    attack = 0,
    sustain = 0,
    release = 0.1,
    shape = 0,
    shapeCurve = 1,
    slide = 0,
    deltaSlide = 0,
    pitchJump = 0,
    pitchJumpTime = 0,
    repeatTime = 0,
    noise = 0,
    modulation = 0,
    bitCrush = 0,
    delay = 0,
    sustainVolume = 1,
    decay = 0,
    tremolo = 0,
    filter = 0,
  ] = params;
  const PI2 = Math.PI * 2;
  const abs = Math.abs;
  const sign = (v: number): number => (v < 0 ? -1 : 1);
  const startSlide = (slide *= (500 * PI2) / sampleRate / sampleRate);
  let startFrequency = (frequency *= ((1 + randomness * 2 * rand() - randomness) * PI2) / sampleRate);
  let modOffset = 0;
  let repeat = 0;
  let crush = 0;
  let jump = 1;
  let t = 0;
  let i = 0;
  let s = 0;
  let f: number;

  // Biquad LP/HP filter.
  const quality = 2;
  const w = (PI2 * abs(filter) * 2) / sampleRate;
  const cos = Math.cos(w);
  const alpha = Math.sin(w) / 2 / quality;
  const a0 = 1 + alpha;
  const a1 = (-2 * cos) / a0;
  const a2 = (1 - alpha) / a0;
  const b0 = (1 + sign(filter) * cos) / 2 / a0;
  const b1 = -(sign(filter) + cos) / a0;
  const b2 = b0;
  let x2 = 0;
  let x1 = 0;
  let y2 = 0;
  let y1 = 0;

  attack = attack * sampleRate || 9; // no pop when attack is 0
  decay *= sampleRate;
  sustain *= sampleRate;
  release *= sampleRate;
  delay *= sampleRate;
  deltaSlide *= (500 * PI2) / sampleRate ** 3;
  modulation *= PI2 / sampleRate;
  pitchJump *= PI2 / sampleRate;
  pitchJumpTime *= sampleRate;
  repeatTime = (repeatTime * sampleRate) | 0;

  const length = (attack + decay + sustain + release + delay) | 0;
  const b = new Float32Array(length);
  for (; i < length; b[i++] = s * volume) {
    if (!(++crush % ((bitCrush * 100) | 0))) {
      s = shape
        ? shape > 1
          ? shape > 2
            ? shape > 3
              ? shape > 4
                ? ((t / PI2) % 1 < shapeCurve / 2 ? 1 : 0) * 2 - 1 // square duty
                : Math.sin(t ** 3) // noise
              : Math.max(Math.min(Math.tan(t), 1), -1) // tan
            : 1 - ((((2 * t) / PI2) % 2) + 2) % 2 // saw
          : 1 - 4 * abs(Math.round(t / PI2) - t / PI2) // triangle
        : Math.sin(t); // sin

      s =
        (repeatTime ? 1 - tremolo + tremolo * Math.sin((PI2 * i) / repeatTime) : 1) *
        (shape > 4 ? s : sign(s) * abs(s) ** shapeCurve) *
        (i < attack
          ? i / attack
          : i < attack + decay
            ? 1 - ((i - attack) / decay) * (1 - sustainVolume)
            : i < attack + decay + sustain
              ? sustainVolume
              : i < length - delay
                ? ((length - i - delay) / release) * sustainVolume
                : 0);

      s = delay ? s / 2 + (delay > i ? 0 : ((i < length - delay ? 1 : (length - i) / delay) * b[(i - delay) | 0]!) / 2 / volume) : s;

      if (filter) s = y1 = b2 * x2 + b1 * (x2 = x1) + b0 * (x1 = s) - a2 * y2 - a1 * (y2 = y1);
    }

    f = (frequency += slide += deltaSlide) * Math.cos(modulation * modOffset++);
    t += f + f * noise * (((i * i * PI2) % 2) - 1);

    if (jump && ++jump > pitchJumpTime) {
      frequency += pitchJump;
      startFrequency += pitchJump;
      jump = 0;
    }

    if (repeatTime && !(++repeat % repeatTime)) {
      frequency = startFrequency;
      slide = startSlide;
      jump ||= 1;
    }
  }
  return b;
}
